import { useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { toast } from "sonner";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import type { PreflightReportSummary } from "../../types/preflight.types";
import { ChangeReportMarketDialog } from "../ChangeReportMarketDialog/ChangeReportMarketDialog";
import { MarketIcon } from "../MarketIcon/MarketIcon";

export interface ReportMarketCellProps {
  report: Pick<PreflightReportSummary, "id" | "market">;
  /** Parent row is hovered — reveals the pencil trigger. */
  isRowHovered?: boolean;
}

/**
 * Market column with a hover pencil that opens the change-market dialog,
 * mirroring rename on the report name cell.
 */
export function ReportMarketCell({
  report,
  isRowHovered = false,
}: ReportMarketCellProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [isLocalHovered, setIsLocalHovered] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const canChangeMarket = permissions.PREFLIGHT_RENAME_REPORT.isOn;
  const showEditTrigger =
    canChangeMarket && (isRowHovered || isLocalHovered || isDialogOpen);
  const market = report.market;
  const marketName = market?.name ?? t("preflight.markets.none", "No market");

  function handleOpenEdit(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    if (!canChangeMarket) {
      toast.error(
        t(
          "preflight.toast.changeMarketForbidden",
          "You don't have permission to change report markets",
        ),
      );
      return;
    }
    setIsDialogOpen(true);
  }

  return (
    <>
      <div
        className="flex min-w-0 w-full items-center gap-1.5"
        onMouseEnter={() => setIsLocalHovered(true)}
        onMouseLeave={() => setIsLocalHovered(false)}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                "flex min-w-0 flex-1 items-center gap-2",
                canChangeMarket && "cursor-pointer",
              )}
              onClick={canChangeMarket ? handleOpenEdit : undefined}
            >
              {market ? (
                <MarketIcon
                  kind={market.kind}
                  code={market.code}
                  name={market.name}
                />
              ) : null}
              <span
                className={cn(
                  "truncate text-sm",
                  market ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {marketName}
              </span>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">{marketName}</TooltipContent>
        </Tooltip>
        {canChangeMarket ? (
          <button
            type="button"
            title={t("preflight.changeMarket.title", "Change report market")}
            aria-label={t("preflight.changeMarket.trigger", "Change market")}
            className={cn(
              "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded border transition-opacity group-hover/row:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:min-h-5 [@media(hover:hover)_and_(pointer:fine)]:min-w-5",
              showEditTrigger
                ? "opacity-100"
                : "pointer-events-none opacity-0",
            )}
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleOpenEdit}
          >
            <Icon name="pencil" size="xs" />
          </button>
        ) : null}
      </div>
      {isDialogOpen ? (
        <ChangeReportMarketDialog
          report={report}
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          trigger={null}
        />
      ) : null}
    </>
  );
}
